import { useState } from 'react';
import { VBX, VBY, ChartAxes, ChartHoverSlices, ChartTooltip, chartFormatDay, spacedTicks, ChartFrame, ChartNote, ChartSkeleton, BAR_SLOT_INSET, ChartStatusLegend, type ChartLegendBand } from '../bookings/chartParts';
import { AREA_SERIES } from '../bookings/constants';
import { fmtEuro } from '../../lib/money';
import { fmtEurShort } from '../finances/financesData';
import type { FinanceDay } from '@bbbk/schema';

// Money per day on the Daily page: value EARNED — what the nights served that
// day were worth.
//
// ONE series. This was two bars for a while, earned against money received; the
// cash side was removed before it shipped (Freddi, 2026-07-30) so the page asks
// one question. The figure itself was meaningless until revenue stopped being
// recognised whole in a stay's check-in month, which would have put a stay's
// entire value on its arrival day and nothing on the nights it served.

// ONE band, built from the area chart's own Big spec so the colour is shared
// rather than restated. Two charts a single dropdown apart should not speak
// different colour languages, and a literal token here would drift the first
// time the palette moved.
const BANDS: ChartLegendBand[] = [
  { key: 'earned', label: 'Earned', fill: AREA_SERIES.find((s) => s.key === 'big')!.fill },
];
const FILL = BANDS[0]!.fill;

// What the hover card says, as a pure function so the wording is testable
// without a hover. It used to read "Earned €1,610" — a label that restates the
// bar's own height. It now leads with the amount and says WHERE IT CAME FROM.
//
// A "▲ 9% on the window average" line was built here and cut (Freddi,
// 2026-07-30). A ranking says the day was good without saying why, and the
// composition line below already answers what the ranking only poses. It is
// also why this takes ONE day rather than the series — nothing here needs to
// see the other bars.
export interface MoneyTooltip { head: string; lead: string; notes: string[] }

export function moneyTooltip(d: FinanceDay): MoneyTooltip {
  // Tense-NEUTRAL wording ("14 dogs · 9 stays", never "in residence"): this
  // chart runs three months forward, so most nights it draws are stays booked
  // rather than dogs currently here.
  //
  // These count every non-cancelled stay spanning the night — the same set
  // `earned` is computed from, so the explanation can never describe different
  // bookings from the figure it explains. Note that is a WIDER set than the
  // desk's residents count, which only counts stays marked arrived.
  //
  // Guarded on dogs, not on earned: a €0 night with dogs booked is a real (if
  // alarming) state, and dividing by no dogs is how a card reads "€NaN".
  const notes = d.dogs > 0
    ? [
        `${d.dogs} ${d.dogs === 1 ? 'dog' : 'dogs'} · ` +
          `${d.stays} ${d.stays === 1 ? 'stay' : 'stays'} · ` +
          `${fmtEuro(Math.round(d.earned / d.dogs))} per dog`,
      ]
    : ['No stays that night'];

  return { head: chartFormatDay(d.date), lead: fmtEuro(d.earned), notes };
}

// The card's title, in one place: the frame renders it in every state, so a
// second literal would be a second thing to keep in step.
const TITLE = 'Money per day';

export interface MoneyChartProps {
  days: FinanceDay[] | null;
  // How many days the window being fetched covers. The skeleton draws that many
  // placeholders, so they are the WIDTH the real bars will be — a fixed count
  // drew a handful of fat columns that then became ninety narrow ones.
  expectedDays: number;
  // Paging, where the caller offers it — the phone tier's fortnight. Absent, a
  // drag does nothing and only the hold reads a day, exactly as in the
  // occupancy charts.
  onSwipe?: (dir: 1 | -1) => void;
  pending?: boolean;
  // Set when the viewer may not see takings, or the fetch failed. Kept distinct
  // from "no data": an empty window is a fact, a refused one is not.
  unavailable?: boolean;
}

export function MoneyChart({ days, expectedDays, pending, unavailable, onSwipe }: MoneyChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const frame = { title: TITLE, legend: <ChartStatusLegend bands={BANDS} /> };

  // Refused is not the same as empty and must never read as "you made nothing".
  if (unavailable) {
    return (
      <ChartFrame {...frame}>
        <ChartNote>Takings aren&rsquo;t available for your account.</ChartNote>
      </ChartFrame>
    );
  }
  if (!days) {
    return <ChartFrame {...frame} busy><ChartSkeleton bars={expectedDays} /></ChartFrame>;
  }
  if (days.length === 0) {
    return <ChartFrame {...frame}><ChartNote>No data in this range.</ChartNote></ChartFrame>;
  }

  // A floor of 5 keeps an all-zero window from collapsing the axis to nothing,
  // matching useChartGeometry's rule for the occupancy charts.
  const yMax = Math.max(5, ...days.map((d) => d.earned));
  // Earned cannot be negative — a night served is worth what it is worth. The
  // floor is kept because the bars are drawn FROM the zero line rather than from
  // the canvas top, and that arithmetic wants an explicit zero.
  const yMin = Math.min(0, ...days.map((d) => d.earned));
  const span = yMax - yMin;
  const yAt = (v: number) => VBY - ((v - yMin) / span) * VBY;
  const zeroY = yAt(0);
  const yTicks = Array.from({ length: 5 }, (_, i) => Math.round(yMin + (span * i) / 4));
  // The shared label rule — regular ticks plus the last one, with the regular
  // tick beside it dropped so the two dates cannot print on top of each other.
  const xTicks = spacedTicks(days.length, Math.ceil(days.length / 10))
    .map(({ i }) => ({ i, label: chartFormatDay(days[i]!.date) }));

  const slotW = VBX / days.length;
  const inset = slotW * BAR_SLOT_INSET;
  const barW = Math.max(0.25, slotW - inset * 2);
  const hovered = hoverIdx != null ? days[hoverIdx] : null;
  const card = hovered ? moneyTooltip(hovered) : null;
  const leftPct = hoverIdx != null ? ((hoverIdx * slotW + slotW / 2) / VBX) * 100 : 0;

  // `pending` here is a date STEP, not a cold load: the bars on screen are stale
  // rather than absent. They are held and dimmed — flashing back to a skeleton
  // would read as the chart having failed.
  return (
    <ChartFrame {...frame} dim={pending}>
      <div className="chart-svg-wrap" onMouseLeave={() => setHoverIdx(null)}>
          <svg
            className="chart-svg"
            width="100%"
            height="100%"
            viewBox={`0 0 ${VBX} ${VBY}`}
            preserveAspectRatio="none"
            role="img"
            aria-label="Money earned per day"
          >
            {yTicks.map((v, i) => (
              <line key={'g' + i} x1="0" y1={yAt(v)} x2={VBX} y2={yAt(v)}
                stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            ))}
            {/* The zero line, drawn stronger, so a bar hanging below it reads as
                money going out rather than as a short bar. */}
            {yMin < 0 && (
              <line x1="0" y1={zeroY} x2={VBX} y2={zeroY}
                stroke="var(--border-strong)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            )}
            {days.map((p, i) => {
              const xL = i * slotW + inset;
              const dim = hoverIdx != null && hoverIdx !== i ? 0.55 : 1;
              // Bars grow from the ZERO line in whichever direction the value
              // goes, so a negative day hangs below it instead of vanishing.
              const bar = (v: number, x: number, fill: string) => (
                <rect x={x} y={Math.min(zeroY, yAt(v))} width={barW}
                  height={Math.max(0.15, Math.abs(zeroY - yAt(v)))} fill={fill} />
              );
              return (
                <g key={p.date} style={{ opacity: dim }}>
                  {bar(p.earned, xL, FILL)}
                </g>
              );
            })}
            <ChartHoverSlices
              series={days}
              onIndex={setHoverIdx}
              // The readout belongs to the finger that summoned it — this was
              // the one chart still leaving its amount card on screen after the
              // hold ended.
              onClear={() => setHoverIdx(null)}
              onSwipe={onSwipe}
            />
          </svg>
          {/* The shared tooltip, so the money chart reads exactly like the
              occupancy ones rather than inventing its own box. */}
          <ChartTooltip
            head={card?.head ?? ''}
            lead={card?.lead}
            notes={card?.notes}
            leftPct={leftPct}
          anchorPct={hovered ? Math.max(0, Math.min(100, (yAt(Math.max(hovered.earned, 0)) / VBY) * 100)) : 0}
        />
      </div>
      {/* A euro axis, abbreviated at the thousands scale — the same rule the
          Finances charts use, so €1.8k reads the same on both pages. */}
      <ChartAxes yTicks={yTicks} xTicks={xTicks} seriesLength={days.length} formatY={fmtEurShort} />
    </ChartFrame>
  );
}
